import { type TableColumnType as ColumnType, Tag, notification } from 'antd';
import moment from 'moment-timezone';
import React, { useCallback, useEffect, useState } from 'react';

import CopyableText from '@fmbt/ui/CopyableText';
import { FiRefreshCw } from '@fmbt/ui/icons';
import { useNoticeModal } from '@fmbt/ui/Modal';
import Text, { TextStack } from '@fmbt/ui/Text';
import dataOperationClient from 'clients/dataOperation';
import DefaultButton from 'components/atoms/DefaultButton';
import DefaultTable from 'components/composites/DefaultTable';
import WarningModal from 'components/modals/WarningModal';
import { EM_DASH } from 'constants/index';
import { DATE_TIME_FORMAT } from 'constants/time';
import DataOperation, { DataOperationStatus } from 'types/models/data-operation';

const STATUS_COLORS: Record<DataOperationStatus, string> = {
  [DataOperationStatus.PENDING]: 'blue',
  [DataOperationStatus.IN_PROGRESS]: 'processing',
  [DataOperationStatus.COMPLETED]: 'success',
  [DataOperationStatus.FAILED]: 'error',
  [DataOperationStatus.RESTORING]: 'warning',
  [DataOperationStatus.RESTORED]: 'default'
};

const RESTORE_CONFIRM_FIELDS: {
  label: string;
  getValue: (operation: DataOperation) => string;
}[] = [
    { label: 'Operation UUID:', getValue: (operation) => operation.operationUuid },
    { label: 'Jira Ticket:', getValue: (operation) => operation.jiraTicketId ?? EM_DASH },
    {
      label: 'Source Sample Point SID:',
      getValue: (operation) => operation.parameters.sourceSamplePointSid
    },
    {
      label: 'Target Sample Point SID:',
      getValue: (operation) => operation.parameters.targetSamplePointSid
    }
  ];

function RestoreTabContent(): JSX.Element {
  const userTimezone = moment.tz.guess();
  const [operations, setOperations] = useState<DataOperation[]>([]);
  const [loading, setLoading] = useState(false);
  const [operationToRestore, setOperationToRestore] = useState<DataOperation | null>(null);
  const [restoringUuid, setRestoringUuid] = useState<string | null>(null);
  const { noticeModal, showNotice } = useNoticeModal();

  const fetchOperations = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await dataOperationClient.fetchDataOperations({
        status: DataOperationStatus.COMPLETED
      });

      if (error) {
        showNotice(
          'Failed to Load Operations',
          error.message || 'An error occurred while fetching data operations'
        );
        return;
      }

      if (data) {
        setOperations(data);
      }
    } catch (error) {
      showNotice('Failed to Load Operations', 'An unexpected error occurred. Please try again');
    } finally {
      setLoading(false);
    }
  }, [showNotice]);

  useEffect(() => {
    fetchOperations();
  }, [fetchOperations]);

  const handleConfirmRestore = useCallback(async () => {
    if (!operationToRestore) {
      return;
    }
    const { operationUuid } = operationToRestore;
    setRestoringUuid(operationUuid);
    const { error } = await dataOperationClient.restoreDataTransfer({ operationUuid });
    setRestoringUuid(null);
    setOperationToRestore(null);

    if (error) {
      showNotice(
        'Restore Failed',
        error.message || 'An error occurred while restoring the data transfer operation'
      );
    } else {
      notification.success({
        message: 'Restore Initiated',
        description: `Operation ${operationUuid} restoration has been initiated and will be processed shortly`,
        duration: 5
      });
      fetchOperations();
    }
  }, [fetchOperations, operationToRestore, showNotice]);

  const columns: ColumnType<DataOperation>[] = [
    {
      title: 'Operation UUID',
      dataIndex: 'operationUuid',
      key: 'operationUuid',
      width: 280,
      render: (uuid: string) => (
        <CopyableText>{uuid}</CopyableText>
      )
    },
    {
      title: 'Jira Ticket',
      dataIndex: 'jiraTicketId',
      key: 'jiraTicketId',
      width: 120,
      render: (jiraTicketId: string) => (
        <CopyableText>{jiraTicketId}</CopyableText>
      )
    },
    {
      title: 'Source SID',
      dataIndex: ['parameters', 'sourceSamplePointSid'],
      key: 'sourceSamplePointSid',
      width: 120,
      render: (sourceSamplePointSid: string) => (
        <CopyableText>{sourceSamplePointSid}</CopyableText>
      )
    },
    {
      title: 'Target SID',
      dataIndex: ['parameters', 'targetSamplePointSid'],
      key: 'targetSamplePointSid',
      width: 120,
      render: (targetSamplePointSid: string) => (
        <CopyableText>{targetSamplePointSid}</CopyableText>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: DataOperationStatus) => (
        <Tag color={STATUS_COLORS[status]}>
          {status.toUpperCase()}
        </Tag>
      )
    },
    {
      title: 'Completed At',
      dataIndex: 'completedAt',
      key: 'completedAt',
      width: 180,
      render: (date: number | null) => date
        ? moment(date).tz(userTimezone).format(DATE_TIME_FORMAT.DEFAULT)
        : EM_DASH
    },
    {
      title: 'Restored At',
      dataIndex: 'restoredAt',
      key: 'restoredAt',
      width: 180,
      render: (date: number | null) => date
        ? moment(date).tz(userTimezone).format(DATE_TIME_FORMAT.DEFAULT)
        : EM_DASH
    },
    {
      title: 'Action',
      key: 'action',
      width: 120,
      fixed: 'right',
      render: (_, record: DataOperation) => {
        const isRestoring = restoringUuid === record.operationUuid;

        return (
          <DefaultButton
            type="primary"
            danger
            size="small"
            onClick={() => setOperationToRestore(record)}
            disabled={record.status !== DataOperationStatus.COMPLETED}
            loading={isRestoring}
          >
            Restore
          </DefaultButton>
        );
      }
    }
  ];

  return (
    <>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
        <DefaultButton
          icon={<FiRefreshCw />}
          onClick={fetchOperations}
          loading={loading}
        >
          Refresh
        </DefaultButton>
      </div>
      <DefaultTable<DataOperation>
        columns={columns}
        dataSource={operations}
        rowKey="operationUuid"
        loading={loading}
        empty={
          <Text v="body-muted">No completed data transfer operations found</Text>
        }
        scroll={{ x: 1200 }}
      />
      <WarningModal
        confirmText="Restore"
        isConfirmButtonLoading={!!operationToRestore && restoringUuid === operationToRestore.operationUuid}
        isOpen={!!operationToRestore}
        onConfirm={handleConfirmRestore}
        onDismiss={() => setOperationToRestore(null)}
        title="Confirm Restore"
      >
        {operationToRestore ? (
          <TextStack v="body">
            <Text v="body">Are you sure you want to restore this data transfer operation?</Text>
            <ul style={{ textAlign: 'left', paddingLeft: 16, marginBottom: 0 }}>
              {RESTORE_CONFIRM_FIELDS.map(({ label, getValue }) => (
                <li key={label}>
                  <Text v="body">
                    <Text v="body-emphasis" inline>{label}</Text> {getValue(operationToRestore)}
                  </Text>
                </li>
              ))}
            </ul>
            <Text v="body" danger>This will revert all data changes made by this transfer operation</Text>
          </TextStack>
        ) : undefined}
      </WarningModal>
      {noticeModal}
    </>
  );
}

export default RestoreTabContent;
